import type { FC, ReactNode } from 'react'

import Image from 'next/image'

import { event } from '@/constants/metric'
import { ActionButton } from '@/components/generals/action-button'

export const Hero: FC = (): ReactNode => {
    return (
        <section id='hero' aria-label={event.wordmark} className='relative'>
            <div className='relative px-0 tab:px-[60px]'>
                <div className='relative h-[calc(100svh-180px)] min-h-[480px] w-full overflow-hidden tab:h-[calc(100svh-160px)]'>
                    <div
                        data-load='scale'
                        data-load-delay='0.2'
                        data-load-duration='2.3'
                        className='absolute inset-0'
                    >
                        <div data-hero-media className='absolute inset-0 will-change-transform'>
                            <Image
                                src='/images/hero.png'
                                alt=''
                                fill
                                priority
                                sizes='100vw'
                                className='object-cover'
                            />
                        </div>
                    </div>

                    <div aria-hidden='true' className='absolute inset-0 bg-[#0d0d0d]/25' />

                    <div aria-hidden='true' className='fw-noise absolute inset-0 opacity-[0.08]' />

                    <div
                        data-hero-copy
                        className='relative z-[3] flex h-full flex-col items-center justify-center gap-5 px-5 text-center tab:px-10'
                    >
                        <p
                            data-load='up'
                            data-load-delay='0'
                            data-load-opacity='0.8'
                            className='font-sans text-[13px] font-medium uppercase leading-[1em] tracking-[0.02em] text-ink tab:text-[14px]'
                        >
                            {event.eyebrow}
                        </p>

                        <h1
                            data-load='up'
                            data-load-delay='0.1'
                            className='font-heading text-[65px] font-medium uppercase leading-[0.95em] tracking-[-0.05em] text-ink tab:text-[91px] desk:text-[114px]'
                        >
                            <span className='block'>
                                {event.titleLines[0]}
                            </span>

                            <span className='block'>
                                {event.titleLines[1]}
                            </span>
                        </h1>

                        <p
                            data-load='up'
                            data-load-delay='0.2'
                            className='max-w-[420px] font-body text-[17px] leading-[1.3em] text-ink'
                        >
                            {event.tagline}
                        </p>

                        <div data-load='up' data-load-delay='0.3' className='pt-2'>
                            <ActionButton href='/#day-1' variant='frosted'>
                                View schedule
                            </ActionButton>
                        </div>
                    </div>
                </div>
            </div>

            <div
                data-load='rise'
                data-load-delay='0.2'
                data-load-duration='2.3'
                className='px-0 tab:px-[60px]'
            >
                <div className='border-y border-line'>
                    <div className='fw-container flex min-h-[80px] flex-col items-center justify-center gap-2 py-5 text-center tab:flex-row tab:justify-between tab:gap-6 tab:py-0 tab:text-left'>
                        <p className='font-sans text-[13px] font-medium uppercase leading-[1.3em] tracking-[0.02em] text-ink tab:text-[14px]'>
                            {event.dates}{' '}
                            <span className='text-ink-mute'>
                                {event.venue}
                            </span>
                        </p>

                        <p className='font-sans text-[13px] font-medium uppercase leading-[1.3em] tracking-[0.02em] text-ink tab:text-[14px]'>
                            {event.facts.map((fact, index) => (
                                <span key={fact.label}>
                                    {fact.value}{' '}
                                    <span className='text-ink-mute'>
                                        {fact.label}{index < event.facts.length - 1 ? ', ' : ''}
                                    </span>
                                </span>
                            ))}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    )
}
